/*
 * Save-frame popup ("Save your frame?") — permanent feature, retail configurator only.
 *
 * When a retail customer closes the frame configurator with real progress (size or
 * measurements entered), they're offered an email capture instead of the plain exit
 * confirm. Submitting sends a Klaviyo event carrying a RESUME LINK that rebuilds the
 * whole session (a Klaviyo flow on the metric emails it); opening that link restores
 * their inputs and reopens the configurator.
 *
 * Origin: productionized from the MM05 A/B test's V3 variation (won its keep on client
 * request after the test itself lost — see TEAM-HUB/test-library/MirrorMate/MM05.md).
 * Two names are kept from the test on purpose and must not be renamed:
 *   - the resume URL param `mm05r` — links in already-sent emails keep working;
 *   - the Klaviyo metric `MM05 Saved Frame Progress` (+ its event property names,
 *     `ResumeUrl` / `ProductId` / `Source`) — the existing Klaviyo flow and its email
 *     template ({{ event.ResumeUrl }}) depend on them.
 *
 * Architecture notes (why this file looks the way it does):
 *   - This script is loaded with `defer` BEFORE frame-button.js / frame-model.js in
 *     theme.liquid: its top-level resume loader must write localStorage before
 *     frame-model's connectedCallback restores state from it (deferred scripts execute
 *     in document order).
 *   - All behavior is DOCUMENT-LEVEL delegation: the homepage "Build My Frame" journey
 *     injects the frame model via innerHTML, where inline scripts never execute and
 *     elements are recreated on every frame-style swap.
 *   - The close-button intercept is a CAPTURE-phase listener: frame-model eagerly binds
 *     its own close handler at init (`.bind(this)`), so it can only be pre-empted, not
 *     prototype-patched.
 *   - Popup markup lives in snippets/save-frame-popup.liquid, rendered inside
 *     frame-model (retail only); its overlay / X / skip buttons carry the theme's own
 *     hook attributes and are bound by frame-model's constructor — not by this file.
 */

(function () {
  if (window.saveFrameInit) return; /* once per page */
  window.saveFrameInit = true;

  var STATE_KEYS = [
    'variant-name', 'retail-size',
    'width-parameter', 'width-fraction-parameter',
    'height-parameter', 'height-fraction-parameter',
    'top-space-parameter', 'right-space-parameter', 'bottom-space-parameter', 'left-space-parameter',
    'clip-top', 'clip-right', 'clip-bottom', 'clip-left',
    'edges-parameter', 'is-measurement-later'
  ];

  /* Every restored value flows into the theme's querySelector('option[value="…"]')
     lookups — quotes/backslashes there would throw inside connectedCallback and brick
     the frame model persistently (localStorage survives reload). Real values are only
     numbers, n/8 fractions, Yes/No/true/false, and size names like "Powder Room". */
  var SAFE_VALUE = /^[A-Za-z0-9 \/.\-]{1,40}$/;

  /* Resume-link loader — top-level on purpose: runs before frame-model.js executes, so
     the theme's own handleCacheData() restores the state naturally. Keys are ALLOWLISTED
     and values validated — a crafted link must never write arbitrary localStorage. */
  (function () {
    try {
      var m = location.search.match(/[?&]mm05r=([^&]+)/);
      if (!m) return;
      var b64 = m[1].replace(/-/g, '+').replace(/_/g, '/');
      var payload = JSON.parse(decodeURIComponent(escape(atob(b64))));
      if (!payload || !payload.s) return;
      STATE_KEYS.forEach(function (k) {
        var v = payload.s[k];
        if (typeof v === 'string' && SAFE_VALUE.test(v)) localStorage.setItem(k, v);
      });
      if (/^\d{1,20}$/.test(String(payload.p || ''))) {
        localStorage.setItem('frame-product-id', String(payload.p));
      }
      window.saveFrameResume = true;
    } catch (err) { /* malformed link → normal PDP */ }
  })();

  /* Retail only: B2B accounts get a different 5-step configurator and no popup. */
  function isRetail(fm) {
    var t = (fm.getAttribute('data-user-type') || 'r').toLowerCase();
    return t === '' || t === '-' || t === 'none' || t === 'r';
  }

  /* "Real progress" = something the resume link can actually restore. NOTE:
     `variant-name` is useless as a signal — the theme writes it on every open. */
  function hasProgress() {
    return !!(
      localStorage.getItem('width-parameter') ||
      localStorage.getItem('height-parameter') ||
      localStorage.getItem('retail-size') ||
      localStorage.getItem('is-measurement-later') === 'true'
    );
  }

  function buildResumeUrl(fm) {
    var s = {};
    STATE_KEYS.forEach(function (k) {
      var v = localStorage.getItem(k);
      if (v !== null && v !== '' && v !== 'null' && v !== 'undefined') s[k] = v;
    });
    var payload = { p: fm.getAttribute('data-product-id'), s: s };
    var productInfo = fm.closest('product-info');
    var path = (productInfo && productInfo.getAttribute('data-url')) || location.pathname;
    var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return location.origin + path.split('?')[0] + '?mm05r=' + b64;
  }

  /* Intercept the configurator's close button BEFORE the theme's own handler (eagerly
     bound at init) — capture phase + stopPropagation. Falls through to the theme's
     "Are you sure?" confirm when there's nothing worth saving or the email was already
     captured this session. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('[js-close-btn]');
    if (!btn) return;
    var fm = btn.closest('frame-model');
    if (!fm || !isRetail(fm)) return;
    if (fm.dataset.saveFrameEmailDone || !hasProgress()) return;
    if (!fm.querySelector('#Save-Frame-Popup')) return;
    e.stopPropagation();
    fm.open({}, 'Save-Frame-Popup');
    /* Keyboard/screen-reader users land in the dialog; wait for the slide-in to finish */
    setTimeout(function () {
      var input = fm.querySelector('#Save-Frame-Popup [js-save-frame-email]');
      if (input && fm.querySelector('#Save-Frame-Popup.is-open')) input.focus({ preventScroll: true });
    }, 350);
  }, true);

  /* Escape = the same "stay" as the X / overlay (the theme binds those; it has no key handling) */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var popup = document.querySelector('#Save-Frame-Popup.is-open');
    if (popup) popup.classList.remove('is-open');
  });

  /* Popup submit → Klaviyo identify + track; the metric-triggered Klaviyo flow
     ("MM05 — Saved Frame Resume") sends the email with the resume link. */
  document.addEventListener('submit', function (e) {
    if (!e.target.closest) return;
    var form = e.target.closest('[js-save-frame-form]');
    if (!form) return;
    e.preventDefault();

    var fm = form.closest('frame-model');
    var input = form.querySelector('[js-save-frame-email]');
    var msg = fm.querySelector('[js-save-frame-msg]');
    var skip = fm.querySelector('.save-frame-skip');
    var email = (input.value || '').trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      /* A red border alone told nobody what was wrong (iPhone QA, 2026-09-29) */
      input.classList.add('save-frame-invalid');
      input.setAttribute('aria-invalid', 'true');
      msg.textContent = 'Please enter a valid email address.';
      msg.classList.add('save-frame-error');
      msg.style.display = 'block';
      input.focus();
      return;
    }
    input.classList.remove('save-frame-invalid');
    input.removeAttribute('aria-invalid');
    msg.style.display = 'none';

    /* If onsite Klaviyo never loaded (ad-blocker / network), no email can ever arrive —
       don't claim success and don't wipe their progress. The real klaviyo global is an
       object; only a shim fallback would be an array. */
    if (!window.klaviyo || Array.isArray(window.klaviyo)) {
      msg.textContent = 'We couldn’t save your frame right now — you can keep going where you left off.';
      msg.classList.add('save-frame-error');
      msg.style.display = 'block';
      return;
    }

    var resumeUrl = buildResumeUrl(fm);
    try {
      window.klaviyo.push(['identify', { '$email': email }]);
      window.klaviyo.push(['track', 'MM05 Saved Frame Progress', {
        'ResumeUrl': resumeUrl,
        'ProductId': fm.getAttribute('data-product-id'),
        'Source': 'mm05-exit-popup'
      }]);
    } catch (err) { /* queue call itself can't reject; delivery gaps show in Klaviyo */ }

    form.style.display = 'none';
    if (skip) skip.style.display = 'none';
    msg.textContent = 'Check your inbox — your frame link is on the way.';
    msg.classList.remove('save-frame-error');
    msg.style.display = 'block';
    fm.dataset.saveFrameEmailDone = '1'; /* next close falls through to the theme confirm */

    /* They were leaving — confirm briefly, then exit for real via the theme's own exit
       path (closes everything, wipes local progress — safe, the emailed link carries
       the state). */
    setTimeout(function () {
      if (typeof fm.resetFrame === 'function') {
        fm.resetFrame();
      } else {
        var popup = fm.querySelector('#Save-Frame-Popup');
        if (popup) popup.classList.remove('is-open');
      }
    }, 1400);
  });

  /* ---------------------------------------------------------------------------------
     Resume landing.

     The link is a snapshot of whatever was entered when the popup was submitted. When
     the shopper left before the clearance step, the theme's own restore still jumps to
     the review step ("variant-name + any dimension → review"), where Add to cart stays
     disabled: toggleAtcBtn() needs every step's continue enabled (the clearance step's
     stays disabled until all four selects are set) and an edges radio checked (the
     hidden N/A radio is only auto-checked once a bottom clearance exists). The pencil
     icons are the only way out — not discoverable (client report, 2026-09-23).

     Fix: after the auto-open, if the review step is showing and required inputs are
     missing, send them to the FIRST INCOMPLETE STEP with a "Welcome back" notice — the
     same updateStep() call a review-step pencil makes, but without `edit` status so
     continue walks forward through clips/edges to the review step instead of skipping
     them. If they still reach the review step incomplete (they navigate back, etc.),
     a fallback notice with a "Finish my frame" button sits above the disabled button.
     Steps are found by hook, never by number (numbers shift on the Build-My-Frame door
     and the PDP "Edit frame" path) — the number is read off the target step itself.
     --------------------------------------------------------------------------------- */

  /* Which step still blocks Add to cart, in flow order — or null when the frame is
     complete (or in Buy-Now-Measure-Later mode, which needs none of this). */
  function firstIncompleteStep(fm) {
    if (fm.querySelector('product-form.is-measurement-later')) return null;
    var dimension = fm.querySelector('[js-frame-step][js-dimension-step]');
    if (dimension && (dimension.querySelector('.is-unselected') || dimension.querySelector('[js-unavailable-size]'))) return dimension;
    var space = fm.querySelector('[js-frame-step][is="space-dimension-step"]');
    if (space && space.querySelector('.is-unselected')) return space;
    var edges = fm.querySelector('[js-frame-step][js-edges-step]');
    if (edges && !edges.classList.contains('hidden') && !fm.querySelector("[data-name='properties[_edges_align]']:checked")) return edges;
    return null;
  }

  function noticeFrom(fm, kind) {
    var tpl = fm.querySelector('template[js-save-frame-notice="' + kind + '"]');
    return tpl && tpl.content.firstElementChild ? tpl.content.firstElementChild.cloneNode(true) : null;
  }

  function removeNotices(fm) {
    fm.querySelectorAll('.save-frame-resume-notice').forEach(function (n) { n.remove(); });
  }

  /* Jump to `target` the way a review-step pencil does (forward walk, see above) and
     put the "Welcome back" banner at the top of that step. */
  function goToStep(fm, target) {
    if (typeof fm.updateStep !== 'function') return false;
    var step = parseInt(target.getAttribute('data-step'), 10);
    if (isNaN(step)) return false;
    fm.updateStep('upgrading', step);
    var host = target.querySelector('.meta-contents');
    if (host && !target.querySelector('.save-frame-resume-notice')) {
      var notice = noticeFrom(fm, 'step');
      if (notice) host.insertBefore(notice, host.firstChild);
    }
    return true;
  }

  /* Fallback: review step showing while inputs are missing → notice + button above
     the disabled Add to cart. Removed as soon as the frame is complete or the session
     ends (exit / add-to-cart wipe localStorage). */
  function syncReviewNotice(fm) {
    var review = fm.querySelector('[js-frame-step][is="last-step"]');
    if (!review) return;
    var existing = review.querySelector('.save-frame-resume-notice');
    if (!hasProgress()) { removeNotices(fm); return; }
    var target = review.classList.contains('is-active') ? firstIncompleteStep(fm) : null;
    if (!target) {
      if (existing) existing.remove();
      if (review.classList.contains('is-active')) removeNotices(fm); /* complete: done */
      return;
    }
    if (existing) return;
    var notice = noticeFrom(fm, 'review');
    if (!notice) return;
    var anchor = review.querySelector('.frame-price-grid');
    var host = review.querySelector('.meta-contents');
    if (anchor) anchor.insertAdjacentElement('beforebegin', notice);
    else if (host) host.insertBefore(notice, host.firstChild);
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('[js-save-frame-resume-btn]');
    if (!btn) return;
    var fm = btn.closest('frame-model');
    if (!fm) return;
    var target = firstIncompleteStep(fm);
    if (target) goToStep(fm, target);
    else removeNotices(fm);
  });

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.saveFrameResume) return;
    setTimeout(function () {
      var btn = document.querySelector('frame-button[is="product"]');
      if (btn) btn.click(); /* theme opens the PDP frame-model, state already restored */

      var fm = document.querySelector('frame-model');
      if (!fm || !isRetail(fm) || !fm.querySelector('template[js-save-frame-notice]')) return;

      var review = fm.querySelector('[js-frame-step][is="last-step"]');
      if (review && review.classList.contains('is-active')) {
        var target = firstIncompleteStep(fm);
        if (target) goToStep(fm, target);
      }

      /* Step changes are class toggles on the step elements — watch them for the
         review fallback; stops itself once the frame is complete or the session ends. */
      if (!window.MutationObserver) return;
      var observer = new MutationObserver(function (records) {
        var stepChanged = records.some(function (r) { return r.target.hasAttribute && r.target.hasAttribute('js-frame-step'); });
        if (!stepChanged) return;
        syncReviewNotice(fm);
        var reviewActive = review && review.classList.contains('is-active');
        if (!hasProgress() || (reviewActive && !firstIncompleteStep(fm))) {
          removeNotices(fm);
          observer.disconnect();
        }
      });
      observer.observe(fm, { attributes: true, attributeFilter: ['class'], subtree: true });
      syncReviewNotice(fm);
    }, 600);
  });
})();
